// BPFA 100 Club — shared design tokens
// Single source of truth for colour, type and spacing.
// Import into any page:  import { colours, font, type, space, card, badge } from '../theme';

export const colours = {
  // Core
  teal:      '#3e5859',   // primary brand / header
  tealDeep:  '#4a6666',   // hover / secondary teal
  tealSoft:  '#5b7d78',
  ink:       '#1f2724',   // headings, primary text
  sage:      '#7a9b5e',   // accent / positive
  sageDeep:  '#3f6030',   // "active" text
  sageSoft:  '#96ad84',

  // Surfaces
  paper:     '#f7f4ed',   // page background
  card:      '#fffdf8',   // card background
  cardAlt:   '#f4f1e7',   // alternate row / subtle fill
  hairline:  '#ddd7c9',   // 1px borders
  hairSoft:  '#eee9dd',   // lighter divider
  hairDeep:  '#d7d1c3',   // stronger border

  // Text
  body:      '#4e5652',   // body copy
  muted:     '#6c7470',   // secondary / labels
  onTeal:    '#f7f4ed',   // text on teal
  onTealDim: '#9db3ac',   // dim text on teal
  onTealSub: '#dfe7e2',   // subtle text on teal

  // Status
  activeBg:  '#eaf0e2',
  activeFg:  '#3f6030',
  lapsedBg:  '#f7e7d3',
  lapsedFg:  '#9c4221',
  neverBg:   '#e8e6e0',
  neverFg:   '#6c7470',

  // Chart series
  chart: ['#3e5859', '#7a9b5e', '#c9a227', '#9db3ac'],
};

export const font = {
  // Newsreader for headings + all money figures; Instrument Sans for UI.
  serif: "'Newsreader', Georgia, serif",
  sans:  "'Instrument Sans', system-ui, -apple-system, sans-serif",
};

// Type scale from the design: 44 · 30 · 26 · 22 · 19 · 16 · 15 · 14 · 13 · 12 · 11
export const type = {
  display: { fontFamily: font.serif, fontSize: '44px', fontWeight: 500, lineHeight: 1.05, letterSpacing: '-0.01em', color: colours.ink },
  h1:      { fontFamily: font.serif, fontSize: '30px', fontWeight: 500, lineHeight: 1.1,  color: colours.ink },
  h2:      { fontFamily: font.serif, fontSize: '26px', fontWeight: 500, lineHeight: 1.15, color: colours.ink },
  h3:      { fontFamily: font.serif, fontSize: '22px', fontWeight: 500, lineHeight: 1.2,  color: colours.ink },
  money:   { fontFamily: font.serif, fontSize: '30px', fontWeight: 500, color: colours.ink },
  lead:    { fontSize: '17px', lineHeight: 1.5, color: colours.body },
  body:    { fontSize: '15px', lineHeight: 1.55, color: colours.body },
  small:   { fontSize: '13px', lineHeight: 1.5, color: colours.muted },
  tiny:    { fontSize: '11px', color: colours.muted },
  // Uppercase tracked label used throughout the design
  eyebrow: { fontSize: '12px', letterSpacing: '0.14em', textTransform: 'uppercase', color: colours.muted },
};

// 4pt base scale
export const space = { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '20px', xxl: '26px', xxxl: '32px' };

export const radius = { sm: '2px', md: '3px', lg: '4px', pill: '999px' };

// Card: 1px hairline, 3px radius, NO shadow (per design system)
export const card = {
  background: colours.card,
  border: `1px solid ${colours.hairline}`,
  borderRadius: radius.md,
  padding: '26px 28px',
};

export const button = {
  primary: {
    background: colours.teal,
    color: colours.onTeal,
    border: 'none',
    borderRadius: radius.md,
    padding: '13px 24px',
    fontFamily: font.sans,
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: '44px',           // mobile tap target
  },
  ghost: {
    background: 'transparent',
    color: colours.onTeal,
    border: '1px solid #6d8887',
    borderRadius: radius.md,
    padding: '9px 15px',
    fontFamily: font.sans,
    fontSize: '14px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  secondary: {
    background: 'transparent',
    color: colours.teal,
    border: `1px solid ${colours.hairDeep}`,
    borderRadius: radius.md,
    padding: '11px 20px',
    fontFamily: font.sans,
    fontSize: '14px',
    cursor: 'pointer',
  },
};

// Status pills: 'active' | 'lapsed' | 'never'
const pillBase = {
  display: 'inline-block',
  padding: '3px 11px',
  borderRadius: radius.pill,
  fontSize: '12px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

export const badge = {
  active: { ...pillBase, background: colours.activeBg, color: colours.activeFg },
  lapsed: { ...pillBase, background: colours.lapsedBg, color: colours.lapsedFg },
  never:  { ...pillBase, background: colours.neverBg,  color: colours.neverFg  },
};

// --- formatting helpers (used everywhere money appears) --------------------

export const gbp = (n) =>
  '£' + Number(n || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const num = (n) => Number(n || 0).toLocaleString('en-GB');

export const shortDate = (d) =>
  new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export const monthYear = (d) =>
  new Date(d).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

const theme = { colours, font, type, space, radius, card, button, badge, gbp, num, shortDate, monthYear };
export default theme;